<template>
  <section class="tabular-browser" aria-label="Dataset row browser">
    <header class="browser-header">
      <h3>{{ dataset.title || 'Dataset rows' }}</h3>
      <span class="status-badge">{{ dataset.status || 'Status unavailable' }}</span>
    </header>
    <p v-if="!ready" role="status">Rows are available only when this dataset is ready. Dataset status is managed by the parent.</p>
    <p v-else-if="!identityValid" role="alert">Sign in to browse this dataset.</p>
    <p v-else-if="loadingMetadata" role="status">Loading immutable schema metadata…</p>
    <div v-else-if="metadataError" role="alert">
      <pre class="error-text">{{ metadataError }}</pre>
      <button type="button" @click="resetContext">Retry metadata</button>
    </div>
    <template v-else-if="columns.length">
      <form class="query-form" @submit.prevent="runQuery(false)">
        <fieldset>
          <legend>Projection — {{ projection.length }} / 128 columns</legend>
          <div class="projection-actions">
            <button type="button" @click="projection = columns.slice(0, 128).map(column => column.key)">Select all columns</button>
            <button type="button" :disabled="!projection.length" @click="projection = []">Unselect all columns</button>
            <span v-if="columns.length > 128" class="hint">Select all chooses the first 128 columns (API limit).</span>
          </div>
          <div class="projection-list">
            <label v-for="column in columns" :key="column.key" class="column-chip">
              <input v-model="projection" type="checkbox" :value="column.key" :disabled="projection.length >= 128 && !projection.includes(column.key)">
              {{ label(column) }} <small>{{ column.logical_type }}</small>
            </label>
          </div>
        </fieldset>
        <fieldset>
          <legend>Filters — {{ filters.length }} / 32</legend>
          <p class="hint">Filters may use unprojected columns. IN accepts a JSON array of 1–100 typed values. Decimal filters are unsupported pending exact serialization.</p>
          <div v-for="(filter, index) in filters" :key="index" class="filter-row">
            <select v-model="filter.column_key" :aria-label="`Filter ${index + 1} column`" @change="resetFilter(filter)">
              <option value="" disabled>Select column</option>
              <option v-for="column in columns" :key="column.key" :value="column.key" :disabled="!operators(column).length">{{ label(column) }} — {{ column.logical_type }}</option>
            </select>
            <select v-model="filter.op" :aria-label="`Filter ${index + 1} operator`">
              <option v-for="op in filterOperators(filter)" :key="op" :value="op">{{ op }}</option>
            </select>
            <select v-if="filter.op !== 'is_null' && filter.op !== 'in' && filterType(filter) === 'boolean'" v-model="filter.value" :aria-label="`Filter ${index + 1} boolean value`">
              <option value="" disabled>Choose boolean</option><option value="true">true</option><option value="false">false</option>
            </select>
            <input v-else-if="filter.op !== 'is_null'" v-model="filter.value" type="text" :aria-label="`Filter ${index + 1} value`"
              :placeholder="filter.op === 'in' ? 'JSON array, e.g. [1,2]' : filterType(filter) === 'datetime' ? 'YYYY-MM-DDTHH:mm:ss[Z/offset]' : filterType(filter) === 'date' ? 'YYYY-MM-DD' : 'Typed value'">
            <button type="button" :aria-label="`Remove filter ${index + 1}`" @click="filters.splice(index, 1)">Remove</button>
          </div>
          <button type="button" :disabled="filters.length >= 32" @click="filters.push({ column_key: '', op: 'eq', value: '' })">Add filter</button>
        </fieldset>
        <div class="query-actions">
          <label>Rows per page <input v-model="limit" type="number" min="1" max="1000" step="1"></label>
          <button type="submit" class="primary" :disabled="querying || !!validationError">{{ querying ? 'Querying…' : cursorError ? 'Run fresh query (discard cursor)' : 'Run fresh query' }}</button>
          <button type="button" :disabled="!canNext" @click="runQuery(true)">Next page</button>
          <button v-if="querying" type="button" @click="cancelQuery">Cancel request</button>
        </div>
        <p v-if="validationError" class="error-text" role="status">{{ validationError }}</p>
      </form>
      <TabularDatasetExport :key="contextKey" :dataset="dataset" :entra-id="entraId" :columns="columns" :projection="projection" :filters="filters" />
      <pre v-if="queryError" class="error-text" role="alert">{{ queryError }}</pre>
      <p v-if="cursorError" role="alert">The continuation cursor cannot be reused. Run a fresh query explicitly; no automatic replay was attempted.</p>
      <template v-if="page">
        <div class="page-summary" role="status">
          Page {{ pageNumber }} · {{ page.rows.length }} returned rows · scanned_rows: {{ page.scanned_rows }} ·
          has_more: {{ page.has_more }} · scan_limit_reached: {{ page.scan_limit_reached }}
          <p v-if="page.scan_limit_reached">The scan limit was reached; a page can contain zero matching rows. Use Next page when a cursor is available.</p>
        </div>
        <div class="table-scroll">
          <table>
            <caption>Current page only — each query replaces the preceding page.</caption>
            <thead><tr><th scope="col">Row ordinal</th><th v-for="column in page.columns" :key="column.column_key" scope="col">{{ label(column) }}<small>{{ column.logical_type }} · {{ column.column_key }}</small></th></tr></thead>
            <tbody><tr v-for="(row, index) in page.rows" :key="index"><th scope="row">{{ row.ordinal }}</th><td v-for="column in page.columns" :key="column.column_key">{{ displayValue(row.values, column.column_key) }}</td></tr></tbody>
          </table>
          <p v-if="!page.rows.length">No matching rows in this page.</p>
        </div>
        <button type="button" class="graph-toggle" aria-haspopup="dialog" @click="showGraph = true"><i class="bi bi-arrows-fullscreen" aria-hidden="true"></i> Open full-screen graph</button>
        <TabularDatasetGraph v-if="showGraph" :title="dataset.title" :columns="page.columns" :rows="page.rows" @close="showGraph = false" />
        <details><summary>Full query and response diagnostics</summary><pre>{{ diagnostics }}</pre></details>
      </template>
    </template>
  </section>
</template>

<script>
import apiService from '@/services/apiService';
import TabularDatasetGraph from '@/components/TabularDatasetGraph.vue';
import TabularDatasetExport from '@/components/TabularDatasetExport.vue';
import { columnLabel, resolveDatasetColumns, operatorsFor, snapshotRowsQuery, rowsQueryPayload,
  validateRowsPage, displayRowValue, rowsErrorMessage } from '@/utils/tabularRows';

export default {
  name: 'TabularDatasetBrowser',
  components: { TabularDatasetGraph, TabularDatasetExport },
  props: {
    dataset: { type: Object, required: true },
    entraId: { type: String, required: true },
  },
  data() {
    return {
      columns: [], projection: [], filters: [], limit: 100, loadingMetadata: false, metadataError: '',
      querying: false, queryError: '', cursorError: false, snapshot: null, page: null, pageNumber: 0, showGraph: false,
    };
  },
  computed: {
    ready() { return this.dataset.status === 'ready'; },
    identityValid() { return typeof this.entraId === 'string' && !!this.entraId.trim(); },
    // Ignore unrelated parent polling fields, but reset on all query-relevant context changes.
    contextKey() { return JSON.stringify([this.dataset.id, this.dataset.schema_id, this.dataset.schema_version_id, this.dataset.status, this.entraId]); },
    draftKey() { return JSON.stringify([this.projection, this.filters, this.limit]); },
    validationError() {
      try { snapshotRowsQuery(this.columns, this.projection, this.filters, this.limit); return ''; }
      catch (error) { return error.message; }
    },
    canNext() { return !this.querying && !this.cursorError && !!this.snapshot && this.page?.has_more === true && this.page.next_cursor != null; },
    diagnostics() { return JSON.stringify({ query: this.snapshot, response: this.page }, null, 2); },
  },
  watch: {
    contextKey: { flush: 'sync', handler() { this.resetContext(); } },
    draftKey: { flush: 'sync', handler() { this.invalidateQuery(); } },
  },
  created() {
    this.disposed = false;
    this.contextGeneration = 0;
    this.queryGeneration = 0;
    this.metadataController = null;
    this.queryController = null;
    this.resetContext();
  },
  beforeUnmount() {
    this.disposed = true;
    ++this.contextGeneration;
    this.metadataController?.abort();
    this.invalidateQuery();
  },
  methods: {
    label: columnLabel,
    operators: operatorsFor,
    displayValue: displayRowValue,
    filterColumn(filter) { return this.columns.find(column => column.key === filter.column_key); },
    filterType(filter) { return this.filterColumn(filter)?.logical_type; },
    filterOperators(filter) { return operatorsFor(this.filterColumn(filter)); },
    resetFilter(filter) { filter.op = this.filterOperators(filter)[0] || ''; filter.value = ''; },
    invalidateQuery() {
      ++this.queryGeneration;
      this.queryController?.abort();
      this.queryController = null;
      this.querying = false;
      this.snapshot = null;
      this.page = null;
      this.pageNumber = 0;
      this.queryError = '';
      this.cursorError = false;
      this.showGraph = false;
    },
    cancelQuery() { this.invalidateQuery(); },
    async resetContext() {
      const generation = ++this.contextGeneration;
      this.metadataController?.abort();
      this.invalidateQuery();
      this.columns = [];
      this.projection = [];
      this.filters = [];
      this.limit = 100;
      this.metadataError = '';
      this.loadingMetadata = false;
      if (!this.ready || !this.identityValid) return;
      if (this.dataset.id == null || this.dataset.schema_id == null || this.dataset.schema_version_id == null) {
        this.metadataError = 'Metadata unavailable: dataset, schema, or immutable schema version reference is missing.';
        return;
      }
      const context = this.contextKey;
      const controller = new AbortController();
      this.metadataController = controller;
      this.loadingMetadata = true;
      const current = () => !this.disposed && !controller.signal.aborted && generation === this.contextGeneration && context === this.contextKey;
      try {
        const response = await apiService.getTabularSchema(this.dataset.schema_id, this.entraId, controller.signal);
        if (!current()) return;
        this.columns = resolveDatasetColumns(this.dataset, response);
        this.projection = this.columns.slice(0, 128).map(column => column.key);
      } catch (error) {
        if (current()) this.metadataError = `Metadata unavailable. ${rowsErrorMessage(error)}`;
      } finally {
        if (current()) this.loadingMetadata = false;
      }
    },
    async runQuery(next = false) {
      if (this.querying || !this.ready || !this.identityValid || !this.columns.length || this.loadingMetadata || this.metadataError) return;
      if (next && !this.canNext) return;
      // Capture context and draft synchronously, not only via scheduled Vue watchers.
      const context = this.contextKey;
      const draft = this.draftKey;
      let snapshot;
      try { snapshot = next ? this.snapshot : snapshotRowsQuery(this.columns, this.projection, this.filters, this.limit); }
      catch (error) { this.queryError = error.message; return; }
      const cursor = next ? this.page.next_cursor : undefined;
      const pageNumber = next ? this.pageNumber + 1 : 1;
      this.queryController?.abort();
      const controller = new AbortController();
      this.queryController = controller;
      const generation = ++this.queryGeneration;
      this.snapshot = snapshot;
      this.querying = true;
      this.queryError = '';
      this.cursorError = false;
      this.page = null;
      const current = () => !this.disposed && !controller.signal.aborted && generation === this.queryGeneration && context === this.contextKey && draft === this.draftKey;
      try {
        const response = await apiService.queryDatasetRows(this.dataset.id, rowsQueryPayload(snapshot, cursor), this.entraId, controller.signal);
        if (!current()) return;
        this.page = validateRowsPage(response, snapshot);
        this.pageNumber = pageNumber;
      } catch (error) {
        if (!current()) return;
        this.queryError = rowsErrorMessage(error);
        // All failed continuations require an explicit fresh query, including expired/conflicting cursors.
        this.cursorError = next;
      } finally {
        if (current()) this.querying = false;
      }
    },
  },
};
</script>

<style scoped>
.tabular-browser { color: #333; display: grid; gap: 14px; }
.browser-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.browser-header h3 { margin: 0; color: #18392b; font-size: 1.15rem; }
.status-badge { border: 1px solid #c8e8d0; border-radius: 12px; background: #e8f5ec; color: #18392b; padding: 3px 12px; }
.query-form { display: grid; gap: 16px; background: #fafaf7; border: 1px solid #deded6; border-radius: 10px; padding: 16px; }
fieldset { border: 0; padding: 0; margin: 0; min-width: 0; }
legend { font-size: .9rem; font-weight: 600; color: #18392b; }
.projection-list { display: flex; flex-wrap: wrap; gap: 8px; max-height: 240px; overflow: auto; }
.projection-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 12px; }
.column-chip { display: flex; align-items: center; gap: 6px; background: white; border: 1px solid #deded6; border-radius: 16px; padding: 4px 10px; font-size: .85rem; }
small, .hint { color: #666; font-size: .78rem; }
.filter-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
.filter-row select:first-child { flex: 1; min-width: 160px; }
.filter-row input { flex: 1; min-width: 160px; }
input, select, button { border: 1px solid #d0d0c8; border-radius: 5px; padding: 6px 10px; max-width: 100%; }
input[type=checkbox] { padding: 0; }
button { background: #f0f0eb; color: #18392b; cursor: pointer; }
button.primary { background: #18392b; color: #fff; }
button:disabled { opacity: .5; cursor: not-allowed; }
.query-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
.query-actions input { width: 100px; }
.error-text { color: #9c2020; white-space: pre-wrap; overflow-wrap: anywhere; }
.page-summary { background: #f6f6f2; padding: 12px; border-radius: 6px; font-size: .85rem; }
.page-summary p { margin: 6px 0 0; }
.table-scroll { overflow: auto; max-height: 520px; border: 1px solid #deded6; border-radius: 6px; }
table { border-collapse: collapse; width: 100%; font-size: .85rem; }
caption { padding: 10px; color: #666; }
th, td { padding: 8px 12px; border-bottom: 1px solid #e8e8e2; text-align: left; white-space: pre-wrap; overflow-wrap: anywhere; min-width: 110px; max-width: 400px; }
thead th { background: #f6f6f2; position: sticky; top: 0; }
th small { display: block; font-weight: normal; }
.graph-toggle { justify-self: start; }
details pre { max-height: 350px; overflow: auto; white-space: pre-wrap; overflow-wrap: anywhere; background: #fafaf7; padding: 12px; }
</style>